import { Accordion, useMobileMediaQuery } from '@iheartjane/reefer';
import type { ReservationMode } from '@iheartjane/types';
import countBy from 'lodash-es/countBy';
import { useEffect, useState } from 'react';

import { trackLoadedCrmPoints } from '@jane/shared-ecomm/tracking';
// eslint-disable-next-line no-restricted-imports
import type {
  CrmDetails,
  CrmIntegration,
  CrmRedemption,
  CrmReward,
  Store,
} from '@jane/shared/models';

import { selectCustomer } from '../../../../../customer/redux/customer';
import { useCustomerSelector } from '../../../../../customer/selectors';
import { useSource } from '../../../../../hooks/useSource';
import { crmDiscountAmount } from '../../../../../lib/cart';
import { reservationModeLabel } from '../../../../../lib/store';
import type { CrmIntegrationResponse } from '../../../../../sources/crmIntegration';
import { getCrmIntegration } from '../../../../../sources/crmIntegration';
import { useCartActions } from '../../../cartFacade/useCartActions';
import { useCartState } from '../../../cartFacade/useCartState';
import {
  getCachedAuthCode,
  removeCachedAuthCode,
  setCachedAuthCode,
} from '../../util/cacheKeys';
import { CustomerLoyaltyPointsContent } from './customerLoyaltyPointsContent';
import { CustomerLoyaltyPointsAccordionHeader } from './customerLoyaltyPointsHeader';

interface LoyaltyPointsProps {
  appliedRedemptions?: readonly CrmRedemption[];
  cartUuid: string;
  headless?: boolean;
  loyaltyPointsStacking: boolean;
  phone: string | null;
  postDiscountSubtotal: number;
  reservationMode: ReservationMode;
  store: Store;
}

const NUM_REWARDS_TO_SHOW = 4;

const flattenRewards = (
  jane_redeemable_rewards: CrmReward[],
  store_redeemable_rewards: CrmReward[]
) => {
  const mapRewards = (rewards: CrmReward[]) =>
    rewards.map(({ reward_name, point_value }) => ({
      reward_name,
      point_value,
    }));
  return mapRewards(jane_redeemable_rewards).concat(
    mapRewards(store_redeemable_rewards)
  );
};

const sortRewards = (rewards: CrmReward[]) =>
  rewards
    .sort(
      (a, b) =>
        a.point_value - b.point_value ||
        a.reward_name.localeCompare(b.reward_name)
    )
    .slice(0, NUM_REWARDS_TO_SHOW);

const getAvailablePoints = (
  points: CrmIntegration['points'],
  appliedRedemptions: readonly CrmRedemption[]
) => {
  if (points === 0) return 0;
  const appliedPoints = appliedRedemptions.reduce(
    (sum, redemption) => (sum += redemption.point_value),
    0
  );

  return points - appliedPoints;
};

const onlineRedeemableRewards = (
  jane_redeemable: CrmReward[],
  availablePoints: number,
  postDiscountSubtotal: number,
  appliedRedemptionCounts: { [crm_reward_id: string]: number }
) => {
  const currentlyRedeemable = jane_redeemable.filter((reward) => {
    const discountAmount = crmDiscountAmount(postDiscountSubtotal, reward);

    const countsValid = reward.max_count
      ? (appliedRedemptionCounts[reward.crm_reward_id] || 0) < reward.max_count
      : true;
    const pointsValid =
      availablePoints >= reward.point_value &&
      discountAmount > 0 &&
      discountAmount <= postDiscountSubtotal;

    return countsValid && pointsValid;
  });
  return sortRewards(currentlyRedeemable);
};

export const CustomerLoyaltyPoints = ({
  cartUuid,
  headless = false,
  loyaltyPointsStacking = true,
  phone,
  postDiscountSubtotal,
  appliedRedemptions = [],
  reservationMode,
  store,
}: LoyaltyPointsProps) => {
  const { id: storeId, name: storeName } = store;
  const isMobile = useMobileMediaQuery({});

  const { applyLoyalty, removeLoyalty } = useCartActions();

  const { isApplyingLoyalty } = useCartState();

  const storeRewardLabel =
    store?.store_compliance_language_settings?.['store_reward'];
  const storeReservationModeLabel = reservationModeLabel(
    store,
    reservationMode
  );
  const storeLoyaltyPointsLabel =
    store?.store_compliance_language_settings?.['loyalty_point'];

  const cacheArgs = { phone, storeId };
  const [authCode, setAuthCode] = useState<string | undefined>(
    getCachedAuthCode(cacheArgs)
  );

  const { email } = useCustomerSelector(selectCustomer);
  // TODO(elliot): Add some cache busting if this auth code for some reason results in an error
  const { loading, data } = useSource<CrmIntegrationResponse>(
    getCrmIntegration(storeId, phone, email, authCode)
  );

  const crmDetails: CrmDetails = {
    crmId: data?.integration_type === 'email' ? email : phone,
    integration_type: data?.integration_type,
  };

  useEffect(() => {
    const cachedAuthCode = getCachedAuthCode(cacheArgs);
    const setCache = !cachedAuthCode || cachedAuthCode !== authCode;
    if (
      authCode &&
      data?.crm_integration?.wallet_state === 'unlocked' &&
      setCache
    ) {
      setCachedAuthCode(cacheArgs, authCode);
    }
  }, [authCode, data?.crm_integration?.wallet_state]);

  useEffect(() => {
    if (!data?.crm_integration) return;
    const {
      crm_integration: { signed_up },
    } = data;

    if (!signed_up) {
      removeCachedAuthCode(cacheArgs);
    }
  }, [data?.crm_integration?.signed_up]);

  useEffect(() => {
    if (!data?.crm_integration) return;
    const {
      crm_integration: {
        crm_provider,
        signed_up,
        points,
        jane_redeemable_rewards,
        store_redeemable_rewards,
      },
    } = data;

    trackLoadedCrmPoints({
      storeHasProvider: true,
      memberPoints: signed_up ? points : null,
      crmProvider: crm_provider,
      crmRewards: flattenRewards(
        jane_redeemable_rewards,
        store_redeemable_rewards
      ),
    });
  }, [data]);

  if (!data?.crm_integration) return null;

  const {
    crm_integration: {
      crm_provider,
      points,
      jane_redeemable_rewards,
      store_redeemable_rewards,
      max_redemption_count,
    },
  } = data;

  const availablePoints = getAvailablePoints(points, appliedRedemptions);
  const appliedRedemptionCounts = countBy(appliedRedemptions, 'crm_reward_id');

  const maxRedemptionCountExceeded = Boolean(
    max_redemption_count && appliedRedemptions.length >= max_redemption_count
  );
  const redeemableOnline = maxRedemptionCountExceeded
    ? []
    : onlineRedeemableRewards(
        jane_redeemable_rewards,
        availablePoints,
        postDiscountSubtotal,
        appliedRedemptionCounts
      );
  const redeemableInStore = sortRewards(store_redeemable_rewards);

  const noRewards = !(redeemableOnline.length || redeemableInStore.length);

  const handleRemoveCrmRedemption = (id: string) => {
    removeLoyalty(id as string | number, crm_provider);
  };

  const handleApplyDiscount = (reward: CrmReward) => {
    if (isApplyingLoyalty) {
      return;
    }
    applyLoyalty({
      crm_reward_id: reward.crm_reward_id,
      point_value: reward.point_value,
      reward: {
        type: reward.reward?.type,
        amount: reward.reward.amount,
      },
      reward_name: reward.reward_name,
      cart_uuid: cartUuid,
      crm_member_points: points,
      crm_provider: reward.crm_provider,
      redemption_url: reward.redemption_payload?.url,
      redemption_checksum: reward.redemption_payload?.checksum,
    });
  };

  return headless ? (
    <CustomerLoyaltyPointsContent
      cartUuid={cartUuid}
      crmIntegration={data.crm_integration}
      error={data.error}
      handleApplyDiscount={handleApplyDiscount}
      loyaltyPointsStacking={loyaltyPointsStacking}
      maxRedemptionCountExceeded={maxRedemptionCountExceeded}
      noRewards={noRewards}
      onUnlockWalletSubmit={setAuthCode}
      crmDetails={crmDetails}
      redeemableInStore={redeemableInStore}
      redeemableOnline={redeemableOnline}
      reservationMode={storeReservationModeLabel}
      storeLoyaltyPointsLabel={storeLoyaltyPointsLabel}
      storeName={storeName}
      unlockLoading={Boolean(authCode) && loading}
    />
  ) : (
    <Accordion.Item id="store-rewards" defaultExpanded>
      <CustomerLoyaltyPointsAccordionHeader
        loading={loading}
        crmIntegration={data.crm_integration}
        availablePoints={availablePoints}
        appliedRedemptions={appliedRedemptions}
        storeRewardLabel={storeRewardLabel}
        handleRemoveCrmRedemption={handleRemoveCrmRedemption}
      />
      <Accordion.Content px={isMobile ? 24 : 40} pb={24} destroyOnClose>
        <CustomerLoyaltyPointsContent
          cartUuid={cartUuid}
          crmIntegration={data.crm_integration}
          error={data.error}
          handleApplyDiscount={handleApplyDiscount}
          loyaltyPointsStacking={loyaltyPointsStacking}
          maxRedemptionCountExceeded={maxRedemptionCountExceeded}
          noRewards={noRewards}
          onUnlockWalletSubmit={setAuthCode}
          crmDetails={crmDetails}
          redeemableInStore={redeemableInStore}
          redeemableOnline={redeemableOnline}
          reservationMode={storeReservationModeLabel}
          storeLoyaltyPointsLabel={storeLoyaltyPointsLabel}
          storeName={storeName}
          unlockLoading={Boolean(authCode) && loading}
        />
      </Accordion.Content>
    </Accordion.Item>
  );
};
